import { Navigate, Route, Routes } from 'react-router-dom';
import { ConfirmProvider } from './lib/confirm.tsx';
import { AdminAcceptInvite } from './auth/AdminAcceptInvite.tsx';
import { AdminLogin } from './auth/AdminLogin.tsx';
import { ForgotPassword } from './auth/ForgotPassword.tsx';
import { ResetPassword } from './auth/ResetPassword.tsx';
import { CompanyLanding } from './auth/CompanyLanding.tsx';
import { CompanyCallback } from './auth/CompanyCallback.tsx';
import { CompanyExpired } from './auth/CompanyExpired.tsx';
import { DriverLanding } from './auth/DriverLanding.tsx';
import { DriverCallback } from './auth/DriverCallback.tsx';
import { NotFound } from './auth/NotFound.tsx';
import { AdminShell } from './admin/AdminShell.tsx';
import { AdminDashboard } from './admin/Dashboard.tsx';
import { AdminCompanies } from './admin/Companies.tsx';
import { AdminCompanyDetail } from './admin/CompanyDetail.tsx';
import { AdminQuestionBuilder } from './admin/QuestionBuilder.tsx';
import { AdminQuestionnaires } from './admin/Questionnaires.tsx';
import { AdminDocuments } from './admin/Documents.tsx';
import { AdminLibrary } from './admin/Library.tsx';
import { AdminDocumentTypes } from './admin/DocumentTypes.tsx';
import { AdminVerifications } from './admin/Verifications.tsx';
import { AdminTraining } from './admin/Training.tsx';
import { AdminAssets } from './admin/Assets.tsx';
import { AdminDrivers } from './admin/Drivers.tsx';
import { AdminDriverDetail } from './admin/DriverDetail.tsx';
import { AdminAssetDetail } from './admin/AssetDetail.tsx';
import { AdminAuditRunner } from './admin/AuditRunner.tsx';
import { AuditorShell } from './admin/AuditorShell.tsx';
import { MyAudits } from './admin/MyAudits.tsx';
import { AdminCorrectiveActions } from './admin/CorrectiveActions.tsx';
import { AdminExpiringDocuments } from './admin/ExpiringDocuments.tsx';
import { AdminSettings } from './admin/Settings.tsx';
import { CompanyShell } from './company/CompanyShell.tsx';
import { CompanyInformation } from './company/CompanyInfo.tsx';
import { CompanyDrivers } from './company/Drivers.tsx';
import { CompanyDriverDetail } from './company/DriverDetail.tsx';
import { Questionnaire } from './company/Questionnaire.tsx';
import { CompanyTraining } from './company/CompanyTraining.tsx';
import { MyDocuments } from './company/MyDocuments.tsx';
import { CompanyLibrary } from './company/Library.tsx';
import { CompanyAssets } from './company/Assets.tsx';
import { CompanyCorrectiveActions } from './company/CorrectiveActions.tsx';
import { DriverShell } from './driver/DriverShell.tsx';
import { DriverQuestionnaire } from './driver/DriverQuestionnaire.tsx';
import { DriverTraining } from './driver/DriverTraining.tsx';
import { DriverLibrary } from './driver/DriverLibrary.tsx';
import { AdminVisitors } from './admin/Visitors.tsx';
import { AdminReports } from './admin/Reports.tsx';
import { VisitorRegister } from './visitor/VisitorRegister.tsx';
import { VisitorProof } from './visitor/VisitorProof.tsx';

export function App() {
  return (
    <ConfirmProvider>
      <Routes>
      <Route path="/" element={<Navigate to="/admin" replace />} />

      {/* Auth */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/forgot-password" element={<ForgotPassword />} />
      <Route path="/admin/reset-password" element={<ResetPassword />} />
      <Route path="/admin/accept-invite" element={<AdminAcceptInvite />} />
      <Route path="/c/:token" element={<CompanyLanding />} />
      <Route path="/c/callback" element={<CompanyCallback />} />
      <Route path="/c/expired" element={<CompanyExpired />} />
      <Route path="/d/:token" element={<DriverLanding />} />
      <Route path="/d/callback" element={<DriverCallback />} />

      {/* Visitor portal (public, no auth) */}
      <Route path="/visit" element={<VisitorRegister />} />
      <Route path="/visit/:id" element={<VisitorProof />} />

      {/* Admin */}
      <Route path="/admin" element={<AdminShell />}>
        <Route index element={<AdminDashboard />} />
        <Route path="companies" element={<AdminCompanies />} />
        <Route path="companies/:id" element={<AdminCompanyDetail />} />
        <Route path="drivers" element={<AdminDrivers />} />
        <Route path="drivers/:id" element={<AdminDriverDetail />} />
        <Route path="assets" element={<AdminAssets />} />
        <Route path="assets/:id" element={<AdminAssetDetail />} />
        <Route path="audits/:id" element={<AdminAuditRunner />} />
        <Route path="corrective-actions" element={<AdminCorrectiveActions />} />
        <Route path="visitors" element={<AdminVisitors />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="questionnaires" element={<AdminQuestionnaires />} />
        <Route path="questions" element={<AdminQuestionBuilder />} />
        <Route path="documents" element={<AdminDocuments />} />
        <Route path="document-types" element={<AdminDocumentTypes />} />
        <Route path="training" element={<AdminTraining />} />
        <Route path="library" element={<AdminLibrary />} />
        <Route path="verifications" element={<AdminVerifications />} />
        <Route path="expiring-documents" element={<AdminExpiringDocuments />} />
        <Route path="users" element={<Navigate to="/admin/settings?tab=users" replace />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="activity" element={<Navigate to="/admin/settings?tab=activity" replace />} />
      </Route>

      {/* Auditor — scoped persona; signs in at /admin/login with email + password */}
      <Route path="/audit" element={<AuditorShell />}>
        <Route index element={<MyAudits />} />
        <Route path="audits/:id" element={<AdminAuditRunner />} />
      </Route>

      {/* Company */}
      <Route path="/c" element={<CompanyShell />}>
        <Route index element={<Navigate to="/c/questionnaire" replace />} />
        <Route path="info" element={<CompanyInformation />} />
        <Route path="questionnaire" element={<Questionnaire />} />
        <Route path="training" element={<CompanyTraining />} />
        <Route path="drivers" element={<CompanyDrivers />} />
        <Route path="drivers/:id" element={<CompanyDriverDetail />} />
        <Route path="documents" element={<MyDocuments />} />
        <Route path="assets" element={<CompanyAssets />} />
        <Route path="corrective-actions" element={<CompanyCorrectiveActions />} />
        <Route path="library" element={<CompanyLibrary />} />
      </Route>

      {/* Driver */}
      <Route path="/d" element={<DriverShell />}>
        <Route index element={<Navigate to="/d/questionnaire" replace />} />
        <Route path="questionnaire" element={<DriverQuestionnaire />} />
        <Route path="training" element={<DriverTraining />} />
        <Route path="library" element={<DriverLibrary />} />
      </Route>

      <Route path="*" element={<NotFound />} />
      </Routes>
    </ConfirmProvider>
  );
}
